/**
 * Cores de dados do design system (docs/design-system.md). Tons sóbrios que convivem com o verde
 * pinho da interface e mantêm contraste sobre fundo claro e escuro.
 */

/** Cores que o usuário escolhe para contas, cartões, categorias e metas. */
export const USER_COLORS: readonly string[] = [
  "#085A42", // pinho
  "#0F766E", // petróleo
  "#2F5D8A", // aço
  "#1E3A5F", // marinho
  "#5B4B8A", // índigo
  "#8A3B6E", // ameixa
  "#A12A2A", // carmim
  "#9A5B3F", // terracota
  "#B45309", // ocre
  "#8A6D1F", // mostarda
  "#4D7C0F", // oliva
  "#57534E", // pedra
]

/** Nomes das cores do usuário, para leitores de tela. */
export const USER_COLOR_NAMES: Record<string, string> = {
  "#085A42": "Pinho", "#0F766E": "Petróleo", "#2F5D8A": "Aço", "#1E3A5F": "Marinho",
  "#5B4B8A": "Índigo", "#8A3B6E": "Ameixa", "#A12A2A": "Carmim", "#9A5B3F": "Terracota",
  "#B45309": "Ocre", "#8A6D1F": "Mostarda", "#4D7C0F": "Oliva", "#57534E": "Pedra",
}

/** Séries de gráficos: receita, despesa, saldo e neutros. */
export const CHART = {
  income: "#0B6B4F",
  expense: "#A12A2A",
  balance: "#0F766E",
  muted: "#D8D5CF",
  grid: "#EFEDEA",
  axis: "#77746E",
  warning: "#B45309",
} as const

/**
 * Fluxo de caixa projetado (ADR-020): compromissos em linha contínua e a estimativa com o dia a dia em ocre tracejado.
 * Par validado (claro e escuro): faixa de luminosidade, croma, separação para daltonismo e contraste.
 */
export const PROJECTION = {
  committed: "#1A7F5E",
  everyday: "#B45309",
} as const

/** Ordem para séries categóricas (várias categorias no mesmo gráfico). */
export const CATEGORICAL = ["#085A42", "#2F5D8A", "#B45309", "#8A3B6E", "#0F766E", "#9A5B3F", "#5B4B8A", "#4D7C0F"] as const

/**
 * Cores da paleta antiga (Tailwind saturado) gravadas no banco: categorias do sistema criadas pelo
 * DatabaseSeeder e cores escolhidas pelos usuários antes do redesign. Convertidas na exibição para o
 * tom equivalente da paleta nova, sem migration de dados.
 */
const LEGACY_COLORS: Record<string, string> = {
  "#EF4444": "#A12A2A", "#DC2626": "#A12A2A", "#F97316": "#9A5B3F", "#EAB308": "#8A6D1F", "#F59E0B": "#B45309",
  "#EC4899": "#8A3B6E", "#DB2777": "#8A3B6E", "#8B5CF6": "#5B4B8A", "#A78BFA": "#5B4B8A", "#7C3AED": "#5B4B8A",
  "#6366F1": "#1E3A5F", "#3B82F6": "#2F5D8A", "#0EA5E9": "#2F5D8A", "#06B6D4": "#0F766E", "#14B8A6": "#0F766E",
  "#10B981": "#085A42", "#22C55E": "#085A42", "#16A34A": "#085A42", "#15803D": "#085A42",
  "#4ADE80": "#4D7C0F", "#BBF7D0": "#4D7C0F", "#A3E635": "#4D7C0F", "#84CC16": "#4D7C0F",
  "#64748B": "#57534E", "#6B7280": "#57534E", "#94A3B8": "#77746E", "#1E293B": "#1C1B19",
}

/** Cor de exibição: converte cores da paleta antiga e mantém as demais. */
export function harmonize(color: string | null | undefined, fallback = "#77746E"): string {
  if (!color) return fallback
  return LEGACY_COLORS[color.toUpperCase()] ?? color
}
